import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { CheckCircle2, CreditCard, Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/EmptyState'
import { invoicingApi } from '@/features/invoicing/api'
import { formatInvoiceMoney } from '@/features/invoicing/format'
import type { PublicInvoice } from '@/features/invoicing/types'
import { formatDate } from '@/lib/locale'

/**
 * Public hosted invoice payment page. No App JWT - the pay-token in the URL
 * is the authorization. Kicks off Stripe Checkout on "Pay with card".
 *
 * Post-Stripe lands back here with ?paid=1 (or ?canceled=1) so the URL
 * remains token-gated and the page just re-renders with the right state.
 */
export function HostedInvoicePaymentPage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const justPaid = searchParams.get('paid') === '1'
  const justCanceled = searchParams.get('canceled') === '1'

  const [invoice, setInvoice] = useState<PublicInvoice | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState(false)

  const loadInvoice = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const inv = await invoicingApi.fetchPublicInvoice(token)
      setInvoice(inv)
      // Only register a "view" when the customer is looking at the invoice
      // for the first time - not when they just landed back from Stripe.
      if (!justPaid && !justCanceled) {
        invoicingApi.pingPublicViewed(token)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load invoice')
    } finally {
      setLoading(false)
    }
  }, [token, justPaid, justCanceled])

  useEffect(() => { loadInvoice() }, [loadInvoice])

  // When Stripe redirected with ?paid=1, the webhook typically settles the
  // invoice to "paid" within a couple of seconds. Poll for up to ~20s so
  // the user sees the final state without a manual refresh.
  useEffect(() => {
    if (!justPaid || !token) return
    let cancelled = false
    let tries = 0
    const tick = async () => {
      if (cancelled) return
      tries += 1
      try {
        const inv = await invoicingApi.fetchPublicInvoice(token)
        if (cancelled) return
        setInvoice(inv)
        if (inv.status === 'paid' || tries >= 10) return
      } catch {
        if (tries >= 10) return
      }
      setTimeout(tick, 2000)
    }
    const t = setTimeout(tick, 2000)
    return () => { cancelled = true; clearTimeout(t) }
  }, [justPaid, token])

  async function onPay() {
    if (!token) return
    setPaying(true)
    try {
      const { url } = await invoicingApi.createPublicCheckout(token)
      window.location.assign(url)
    } catch (e) {
      // Map known server-side codes from the Stripe Connect flow to a
      // friendlier explanation. The backend throws 409 with a body
      // shape { error, code } via PAYMENT_MODE_NOT_SET / CONNECT_*.
      // The api wrapper surfaces { error: <string> } in e.message.
      const raw = e instanceof Error ? e.message : ''
      const friendly = (() => {
        if (/payment collection mode/i.test(raw)) {
          return 'This merchant has not finished setting up online payments yet. Please contact them directly to settle this invoice.'
        }
        if (/onboarding is not yet complete|account not created/i.test(raw)) {
          return 'This merchant is still finishing payment setup with Stripe. Please try again shortly or contact them directly.'
        }
        return raw || 'Failed to start payment'
      })()
      setError(friendly)
      setPaying(false)
    }
  }

  useEffect(() => {
    const prev = document.title
    if (invoice) document.title = `Invoice ${invoice.invoice_number}`
    return () => { document.title = prev }
  }, [invoice])

  const businessAddr = invoice ? [
    invoice.business_address_line1,
    invoice.business_address_line2,
    [invoice.business_city, invoice.business_state, invoice.business_postal_code].filter(Boolean).join(', '),
    invoice.business_country,
    invoice.business_email,
    invoice.business_phone,
  ].filter(Boolean) : []

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-2xl">
        {/* Brand header */}
        <div className="mb-6 flex flex-wrap items-start gap-3">
          {invoice?.logo_url ? (
            <img src={invoice.logo_url} alt={invoice.business_name || 'Logo'} className="h-12 w-auto rounded-sm" />
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="text-sm text-muted-foreground">Invoice from</div>
            <div className="text-lg font-semibold">{invoice?.business_name || 'Bokapsys'}</div>
            {businessAddr.length > 0 && (
              <div className="mt-1 text-xs text-muted-foreground">
                {businessAddr.map((p, i) => <div key={i}>{p}</div>)}
              </div>
            )}
          </div>
        </div>

        {loading && (
          <Card><div className="flex items-center justify-center gap-2 p-10 text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Loading…</div></Card>
        )}

        {error && !loading && (
          <Card>
            <EmptyState
              icon={CreditCard}
              title="Can't load this invoice"
              description={error}
            />
          </Card>
        )}

        {invoice && !loading && (
          <>
            {justPaid && invoice.status !== 'paid' && (
              <Card className="mb-4 border-primary/30 bg-primary/5">
                <div className="flex items-start gap-3 p-4 text-sm">
                  <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-primary" />
                  <div>
                    <div className="font-medium">Payment submitted. Confirming…</div>
                    <div className="text-muted-foreground">This page will update automatically once your bank confirms the charge.</div>
                  </div>
                </div>
              </Card>
            )}
            {justPaid && invoice.status === 'paid' && (
              <Card className="mb-4 border-success/30 bg-success/5">
                <div className="flex items-start gap-3 p-4 text-sm">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                  <div>
                    <div className="font-medium">Payment received. Thank you!</div>
                    <div className="text-muted-foreground">A receipt has been recorded against invoice {invoice.invoice_number}.</div>
                  </div>
                </div>
              </Card>
            )}
            {justCanceled && invoice.status !== 'paid' && (
              <Card className="mb-4">
                <div className="p-4 text-sm text-muted-foreground">
                  Payment canceled. You can try again below.
                </div>
              </Card>
            )}

            <Card>
              <div className="space-y-5 p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Invoice number</div>
                    <div className="text-xl font-bold">{invoice.invoice_number}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Amount due</div>
                    <div className="text-2xl font-bold">
                      {formatInvoiceMoney(invoice.amount_due, invoice.currency)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Billed to</div>
                    <div>{invoice.customer_display_name}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Due date</div>
                    <div>{formatDate(invoice.due_date)}</div>
                  </div>
                  {invoice.po_number && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">PO number</div>
                      <div>{invoice.po_number}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Issue date</div>
                    <div>{formatDate(invoice.issue_date)}</div>
                  </div>
                </div>

                <hr />

                <div className="divide-y rounded-md border text-sm">
                  <div className="grid grid-cols-12 gap-2 bg-muted/40 px-3 py-2 text-xs uppercase text-muted-foreground">
                    <div className="col-span-9">Description</div>
                    <div className="col-span-3 text-right">Amount</div>
                  </div>
                  {invoice.lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2">
                      <div className="col-span-9">{l.description}</div>
                      <div className="col-span-3 text-right">{formatInvoiceMoney(l.amount, invoice.currency)}</div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col items-end gap-1 text-sm">
                  <div>Subtotal: {formatInvoiceMoney(invoice.subtotal, invoice.currency)}</div>
                  {Number(invoice.tax_total) > 0 && <div>Tax: {formatInvoiceMoney(invoice.tax_total, invoice.currency)}</div>}
                  {Number(invoice.discount) > 0 && <div>Discount: -{formatInvoiceMoney(invoice.discount, invoice.currency)}</div>}
                  <div className="text-base font-bold">Total: {formatInvoiceMoney(invoice.total, invoice.currency)}</div>
                  {Number(invoice.amount_paid) > 0 && <div className="text-muted-foreground">Paid: {formatInvoiceMoney(invoice.amount_paid, invoice.currency)}</div>}
                </div>

                {(invoice.notes || invoice.terms || invoice.bank_details) && (
                  <>
                    <hr />
                    <div className="space-y-3 text-sm">
                      {invoice.notes && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-muted-foreground">Notes</div>
                          <div className="whitespace-pre-wrap">{invoice.notes}</div>
                        </div>
                      )}
                      {invoice.terms && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-muted-foreground">Terms</div>
                          <div className="whitespace-pre-wrap">{invoice.terms}</div>
                        </div>
                      )}
                      {invoice.bank_details && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-muted-foreground">Bank details</div>
                          <div className="whitespace-pre-wrap">{invoice.bank_details}</div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Card>

            <div className="mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:justify-end">
              {invoice.status === 'paid' ? (
                <div className="rounded-md border bg-card px-4 py-2 text-center text-sm text-muted-foreground">
                  This invoice has been paid. Thank you!
                </div>
              ) : invoice.payment_collection_method == null ? (
                // No online payment method on the invoice → bank-transfer
                // only. Suppress the Pay button and the "Choose card / ACH"
                // copy. The bank_details block above is the call-to-action.
                <div className="rounded-md border bg-muted/40 px-4 py-3 text-center text-sm text-muted-foreground">
                  {invoice.bank_details
                    ? 'This invoice is payable by bank transfer. Use the bank details above to pay.'
                    : 'Online payment is not available for this invoice. Please contact the business directly to arrange payment.'}
                </div>
              ) : (
                <>
                  <Button size="lg" onClick={onPay} disabled={paying || justPaid}>
                    {paying || justPaid ? <Loader2 className="mr-2 size-4 animate-spin" /> : <CreditCard className="mr-2 size-4" />}
                    Pay {formatInvoiceMoney(invoice.amount_due, invoice.currency)}
                  </Button>
                  <p className="mt-2 text-center text-xs text-muted-foreground">
                    Choose card, Apple Pay, Google Pay
                    {invoice.currency?.toUpperCase() === 'USD' ? ', or US bank transfer (ACH)' : null}
                    {invoice.currency?.toUpperCase() === 'EUR' ? ', or SEPA Direct Debit' : null}
                    {' '}on the next page.
                  </p>
                  {invoice.currency?.toUpperCase() === 'USD' || invoice.currency?.toUpperCase() === 'EUR' ? (
                    <p className="mt-1 text-center text-xs text-muted-foreground">
                      Bank transfers may take 1–4 business days to clear.
                    </p>
                  ) : null}
                </>
              )}
            </div>

            {invoice.status !== 'paid' && invoice.payment_collection_method != null && (
              <div className="mt-8 text-center text-xs text-muted-foreground">
                Secure checkout powered by Stripe. Bokapsys never sees your card or bank details.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
